import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";

import { ForgottenPasswordComponent } from "../modules/forgotten-password/forgotten-password.component";
import { LoginComponent } from "../modules/login/login.component";
import { MaintenanceComponent } from "../modules/maintenance/maintenance.component";
import { OauthCheckrComponent } from "../modules/oauth-checkr/oauth-checkr.component";
import { OauthLinkedInComponent } from "../modules/oauth-linkedin/oauth-linkedin.component";
import { OauthOpenApiComponent } from "../modules/oauth-open-api/oauth-open-api.component";
import { OauthQuestionnaireComponent } from "../modules/oauth-questionnaire/oauth-questionnaire.component";
import { RegisterComponent } from "../modules/register/register.component";
import { ResetPasswordComponent } from "../modules/reset-password/reset-password.component";
import { OpenIdSsoComponent } from "../modules/sso/modules/open-id-sso/open-id-sso.component";
import { SamlSsoComponent } from "../modules/sso/modules/saml-sso/saml-sso.component";
import { TwoFactorComponent } from "../modules/two-factor/two-factor.component";
import { VerifyAccountComponent } from "../modules/verify-account/verify-account.component";
import { AuthGuardService } from "../services/auth-guard/auth-guard.service";
import { MaintenanceGuardService } from "../services/maintenance-guard/maintenance-guard.service";
import { OauthQuestionnaireGuardService } from "../services/oauth-questionnaire-guard/oauth-questionnaire-guard.service";
import { TokenGuardService } from "../services/token-guard/token-guard.service";
import { PlatformGuardService } from "../services/platform-guard/platform-guard.service";
import { SamlMetadataComponent } from "../modules/sso/modules/saml-sso/saml-metadata/saml-metadata.component";
import {UserDetailsComponent} from "../components/user-details/user-details.component";
import {UserVerifyComponent} from "../components/user-verify/user-verify.component";
import {UserLoggedInComponent} from "../components/user-logged-in/user-logged-in.component";
import {OrgCreationComponent} from "../components/org-creation/org-creation.component";
import {PermissionsService} from "../services/permissions/permissions.service";
import { SignupComponent } from "../modules/signup/signup.component";
import {SignupVerifyComponent} from "../modules/signup-verify/signup-verify.component";
import {JoinTeamComponent} from "../modules/join-team/join-team.component";
import {UserDetailsGuard} from "../services/signup-guard/user-detail-guard.service";
import {UserGuard} from "../services/signup-guard/user-guard.service";
import {OrgCreationGuard} from "../services/signup-guard/org-creation-guard.service";

const appRoutes: Routes = [
    {
        path: "",
        component: LoginComponent,
        data: {
            omni: true,
            title: "Xref Login | Avoid bad hires. Retain your best.",
            description:
        "Log in to your Xref account to turn recruitment data into hiring decisions. Access verified reference checks, engagement surveys and insights on one platform.",
        },
    },
    {
        path: 'user-details/:email',
        component: UserDetailsComponent,
        canActivate: [UserDetailsGuard]
    },
    {
        path: 'user-verify',
        component: UserVerifyComponent,
        canActivate: [UserGuard]
    },
    {
        path: 'user-logged-in',
        component: UserLoggedInComponent,
        canActivate: [UserGuard]
    },
    {
        path: 'org-creation',
        component: OrgCreationComponent,
        canActivate: [OrgCreationGuard]
    },
    {
        path: "login",
        component: LoginComponent,
        data: {
            omni: true,
            title: "Xref Login | Avoid bad hires. Retain your best.",
            description:
        "Log in to your Xref account to turn recruitment data into hiring decisions. Access verified reference checks, engagement surveys and insights on one platform.",
        },
    },
    {
        path: "signup",
        component: SignupComponent,
        data: {
            title: "Sign Up to Xref | Start Your Free Trial Today",
            description: "Experience faster hiring with Xref. Start a free trial for automated referencing, employee engagement surveys and data-driven recruitment tools.",
            page: 'welcomePage',
            url: "signup"
        },
    },
    {
        path: "signup/expired",
        component: SignupComponent,
        data: {
            title: "Verification link expired",
            page: 'linkExpiredPage',
            url: "signup"
        },
    },
    {
        path: "signup/manual-upgrade",
        component: SignupComponent,
        data: {
            title: "Almost there!",
            page: 'manualUpgradePage',
            url: "signup"
        },
    },
    {
        path: "signup/join-team",
        component: JoinTeamComponent,
    },
    {
        path: "forgotten-password",
        component: ForgottenPasswordComponent,
        data: { omni: true },
    },
    // Page to handle user requested password reset
    {
        path: "reset-password",
        component: ResetPasswordComponent,
        canActivate: [TokenGuardService, PlatformGuardService],
        data: { omni: true },
    },
    // Page to handle force password changed triggered by the system (ie changed password policy)
    {
        path: "change-password",
        component: ResetPasswordComponent,
        canActivate: [TokenGuardService],
    },
    // Registration form
    {
        path: "register",
        component: RegisterComponent,
        data: {
            title: "Xref | Register for a Free Trial",
            description:
        "Register for a free Xref account and reference your first candidate today, using a complimentary credit covering up to two references.",
        },
    },
    // Signup Verify
    {
        path: "signup/verify",
        component: SignupVerifyComponent,
    },
    // Registered users activate account by clicking tokenised link from email
    {
        path: "verify-account",
        component: VerifyAccountComponent,
    },
    // 2FA process
    {
        path: "two-factor",
        component: TwoFactorComponent,
        canActivate: [TokenGuardService],
        data: { omni: true },
    },
    {
        path: "sso/open-id",
        component: OpenIdSsoComponent,
        canActivate: [],
    },
    {
        path: "sso/saml",
        component: SamlSsoComponent,
        canActivate: [],
        data: { omni: true },
    },
    {
        path: "sso/saml/metadata/:clientId",
        component: SamlMetadataComponent,
        data: { omni: true },
    },
    {
        path: "oauth/checkr",
        component: OauthCheckrComponent,
    },
    {
        path: "oauth/linkedin",
        component: OauthLinkedInComponent,
        canActivate: [AuthGuardService],
    },
    {
        path: "oauth/open-api/:clientId",
        component: OauthOpenApiComponent,
        canActivate: [AuthGuardService],
    },
    {
        path: "oauth/questionnaire",
        component: OauthQuestionnaireComponent,
        canActivate: [OauthQuestionnaireGuardService],
        children: [
            {
                path: ":clientId",
                component: OauthQuestionnaireComponent,
            },
        ],
    },
    {
        path: "maintenance",
        component: MaintenanceComponent,
        canActivate: [MaintenanceGuardService],
    },
    // Catch all
    {
        path: "**",
        component: LoginComponent,
        data: { omni: true },
    },
];

@NgModule({
    imports: [RouterModule.forRoot(appRoutes)],
    exports: [RouterModule],
    providers: [
        MaintenanceGuardService,
        TokenGuardService,
        PlatformGuardService,
        AuthGuardService,
        OauthQuestionnaireGuardService,
    ],
})
export class AppRoutingModule {}
